import { type ApiFetchContext, apiFetch } from "../client";
import type {
  PromoteBonusWallet,
  PromoteCommissionConfig,
  PromoteMonthlyRank,
  PromoteRankEntry,
  PromoteStat,
} from "../types/promote";

/**
 * Thin REST wrappers for the new promote-alliance endpoints
 * (`NewPromoteAllianceController`, `/newPromoteAlliance/*`). All are JWT-gated
 * GETs that return their VO directly in the `result` envelope. We keep the
 * field names aligned with the backend VOs so consumers don't double-translate,
 * and apply only light coercion (null-safety + rate normalisation).
 */

const n = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const nOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/**
 * Normalise a commission rate to a [0,1] fraction. Backend stores decimals
 * (0.03 = 3%), but guard against a value expressed as whole percent (3 = 3%):
 * anything > 1 is treated as a percentage and divided by 100. A genuine
 * fraction (≤ 1) passes through untouched. This keeps the tier ladder and the
 * calculator correct regardless of which scale the backend ships.
 */
export function normalizeRate(raw: unknown): number {
  const v = n(raw);
  if (v <= 0) return 0;
  return v > 1 ? v / 100 : v;
}

/** GET /newPromoteAlliance/listCommissionConfig — public (anon-readable, aps-boot#299) */
export async function listCommissionConfig(
  ctx: ApiFetchContext,
  authToken?: string | null,
): Promise<PromoteCommissionConfig[]> {
  const raw = await apiFetch<Array<Record<string, unknown>>>(
    ctx,
    "/newPromoteAlliance/listCommissionConfig",
    authToken ? { authToken } : {},
  );
  return (raw ?? [])
    .map((r) => ({
      level: n(r.level),
      orderUsersStart: n(r.orderUsersStart),
      orderUsersEnd: nOrNull(r.orderUsersEnd),
      orderBonusRate: normalizeRate(r.orderBonusRate),
      shippingBonusRate: normalizeRate(r.shippingBonusRate),
    }))
    .sort((a, b) => a.level - b.level);
}

/** GET /newPromoteAlliance/getBonusWalletInfo */
export async function getBonusWalletInfo(
  ctx: ApiFetchContext,
  authToken: string,
): Promise<PromoteBonusWallet> {
  const r = await apiFetch<Record<string, unknown>>(ctx, "/newPromoteAlliance/getBonusWalletInfo", {
    authToken,
  });
  return {
    withdrawalLimit: nOrNull(r.withdrawalLimit),
    withdrawnCount: nOrNull(r.withdrawnCount),
    totalBonus: n(r.totalBonus),
    settledBonus: n(r.settledBonus),
    unsettledBonus: n(r.unsettledBonus),
    withdrawnBonus: n(r.withdrawnBonus),
  };
}

/** GET /newPromoteAlliance/getPromoteStatInfo */
export async function getPromoteStatInfo(
  ctx: ApiFetchContext,
  authToken: string,
): Promise<PromoteStat> {
  const r = await apiFetch<Record<string, unknown>>(ctx, "/newPromoteAlliance/getPromoteStatInfo", {
    authToken,
  });
  return {
    totalInvitedUsers: n(r.totalInvitedUsers),
    totalOrderUsers: n(r.totalOrderUsers),
    totalSignUsers: n(r.totalSignUsers),
    orderUsersOfThisMonth: n(r.orderUsersOfThisMonth),
    settledFirstOrderCommissionTotal: n(r.settledFirstOrderCommissionTotal),
    unSettledFirstOrderCommissionTotal: n(r.unSettledFirstOrderCommissionTotal),
    settledGoodsCommissionTotal: n(r.settledGoodsCommissionTotal),
    unSettledGoodsCommissionTotal: n(r.unSettledGoodsCommissionTotal),
    settledExpressCommissionTotal: n(r.settledExpressCommissionTotal),
    unSettledExpressCommissionTotal: n(r.unSettledExpressCommissionTotal),
  };
}

function normalizeRankEntry(r: Record<string, unknown> | null | undefined): PromoteRankEntry {
  return {
    rank: nOrNull(r?.rank),
    userId: (r?.userId as string) ?? null,
    userName: (r?.userName as string) ?? null,
    avatar: (r?.avatar as string) ?? null,
    email: (r?.email as string) ?? null,
    orderUsers: nOrNull(r?.orderUsers),
    totalBonus: n(r?.totalBonus),
    needOrderUsers: nOrNull(r?.needOrderUsers),
    outperformedPercent: nOrNull(r?.outperformedPercent),
  };
}

/** GET /newPromoteAlliance/getMonthlyRankInfo — public top list (anon-readable, aps-boot#299) */
export async function getMonthlyRankInfo(
  ctx: ApiFetchContext,
  authToken?: string | null,
): Promise<PromoteMonthlyRank> {
  const r = await apiFetch<{
    top20List?: Array<Record<string, unknown>>;
    currentUserRankInfo?: Record<string, unknown> | null;
  }>(ctx, "/newPromoteAlliance/getMonthlyRankInfo", authToken ? { authToken } : {});
  return {
    top20List: (r?.top20List ?? []).map(normalizeRankEntry),
    currentUserRankInfo: r?.currentUserRankInfo ? normalizeRankEntry(r.currentUserRankInfo) : null,
  };
}
